import { SourceType as CommonSourceType } from '@aon/tws-common-ui-questions/build/lib/models';
import _ from 'lodash';
import { Reducer } from 'redux';
import ContentConstants from '../contentConstants';
import { IAddedQuestion, ICustomQuestion, IQuestionVariation, ITranslationEditItem, SourceType } from '../models';
import { IQuestionQuery } from '../models/IQuestionQuery';
import * as Actions from './Actions';
import { ActionTypes } from './ActionTypes';
import { ActionTypes as CommonQuestionActionTypes, IQuestionLanguagesBackgroundGetSucceeded } from './CommonQuestionListStore';

export interface IQuestionModalState {
	addedQuestions: IAddedQuestion[];
	disableClose: boolean;
	isDemographic: boolean;
	isFollowUp: boolean;
	isLoading: boolean;
	isOpen: boolean;
	primaryButtonText?: string;
	secondaryButtonText?: string;
	selectedQuestionVariationIds: number[];
	selectedCustomQuestionIds: number[];
	title: string;
}

export const defaultState: IQuestionModalState = {
	addedQuestions: [],
	disableClose: false,
	isDemographic: false,
	isFollowUp: false,
	isLoading: false,
	isOpen: false,
	selectedQuestionVariationIds: [],
	selectedCustomQuestionIds: [],
	title: '',
};

export const ActionCreators = {
	OpenSurveyQuestionsModal: (query: IQuestionQuery) => (dispatch: any) => {
		dispatch({
			type: ActionTypes.SURVEYQUESTIONS_MODAL_OPEN,
			payload: query,
		});
	},
	OpenDemographicQuestionsModal: (query: IQuestionQuery) => (dispatch: any) => {
		dispatch({
			type: ActionTypes.DEMOGRAPHICQUESTIONS_MODAL_OPEN,
			payload: query,
		});
	},
	SubmitQuestionsModal: (
		addedQuestions: IAddedQuestion[],
		selectedQuestionVariationIds: number[],
		selectedCustomQuestionIds: number[],
		isDemographic: boolean,
	) => {
		return {
			type: ActionTypes.SURVEYQUESTIONS_MODAL_SUBMIT,
			payload: {
				addedQuestions,
				selectedQuestionVariationIds,
				selectedCustomQuestionIds,
				isDemographic,
			},
		};
	},
	CloseQuestionsModal: () => {
		return { type: ActionTypes.SURVEYQUESTIONS_MODAL_CLOSE };
	},
};

type KnownActions =
	| Actions.ISurveyQuestionsModalOpen
	| Actions.IDemographicQuestionsModalOpen
	| Actions.ISurveyQuestionsModalSubmit
	| Actions.ILibraryQuestionsSelected
	| Actions.ISurveyQuestionsModalClose
	| Actions.ILibraryQuestionsVariationSelected
	| Actions.ICustomQuestionSelected
	| Actions.ICustomQuestionSaveSucceeded
	| Actions.ICustomQuestionsGetSucceeded
	| Actions.ILibraryQuestionsGetSucceeded
	| IQuestionLanguagesBackgroundGetSucceeded
	| Actions.ICustomQuestionsModalOpen
	| Actions.ICustomDemographicQuestionsModalOpen;

export const reducer: Reducer<IQuestionModalState, KnownActions> = (
	state: IQuestionModalState = defaultState,
	action: KnownActions,
): IQuestionModalState => {
	switch (action.type) {
		case ActionTypes.SURVEYQUESTIONS_MODAL_OPEN:
			return {
				...state,
				...action.payload,
				isOpen: true,
				primaryButtonText: ContentConstants.BUTTONS_FINISH_CLOSE,
				title: ContentConstants.TITLE_SURVEY_QUESTIONS,
			};
		case ActionTypes.CUSTOMQUESTIONS_MODAL_OPEN:
		case ActionTypes.CUSTOMDDEMOGRAPHICQUESTIONS_MODAL_OPEN:
			return {
				...state,
				...action.payload,
			};
		case ActionTypes.DEMOGRAPHICQUESTIONS_MODAL_OPEN:
			return {
				...state,
				...action.payload,
				isOpen: true,
				primaryButtonText: ContentConstants.BUTTONS_FINISH_CLOSE,
				title: ContentConstants.TITLE_DEMOGRAPHIC_QUESTIONS,
			};
		case ActionTypes.SURVEYQUESTIONS_MODAL_SUBMIT:
		case ActionTypes.SURVEYQUESTIONS_MODAL_CLOSE:
			return { ...defaultState, isDemographic: state.isDemographic };
		case ActionTypes.LIBRARY_QUESTION_SELECTED: {
			const { questionId, questionVariationId, questionVariations } = action.payload.question;

			if (action.payload.isSelected) {
				const addedQuestions = state.addedQuestions.filter((q) => q.questionId !== questionId).concat([{ ...action.payload.question }]);

				if (_.isEqual(action.payload.question.sourceType, SourceType.Library)) {
					const altVersionVariationIds = questionVariations != null ? questionVariations.map((q: any) => q.questionVariationId) : [];
					const selectedQuestionVariationIds = state.selectedQuestionVariationIds
						.filter((id) => altVersionVariationIds.indexOf(id) === -1)
						.concat(questionVariationId ? [questionVariationId] : []);

					return { ...state, selectedQuestionVariationIds, addedQuestions };
				} else {
					const selectedCustomQuestionIds = [...state.selectedCustomQuestionIds, questionId];
					return { ...state, selectedCustomQuestionIds, addedQuestions };
				}
			} else {
				if (_.isEqual(action.payload.question.sourceType, SourceType.Library)) {
					const selectedQuestionVariationIds = state.selectedQuestionVariationIds.filter((id) => id !== questionVariationId);
					const addedQuestions = state.addedQuestions.filter((q) => q.questionVariationId !== questionVariationId);
					return { ...state, selectedQuestionVariationIds, addedQuestions };
				} else {
					const selectedCustomQuestionIds = state.selectedCustomQuestionIds.filter((id) => id !== questionId);
					const addedQuestions = state.addedQuestions.filter((q) => q.questionId !== questionId);
					return { ...state, selectedCustomQuestionIds, addedQuestions };
				}
			}
		}
		case ActionTypes.LIBRARY_QUESTIONVARIATION_SELECTED: {
			const { questionVariationId, questionVariations } = action.payload.question;

			if (action.payload.isSelected && questionVariationId !== undefined) {
				const siblingVariationIds = (questionVariations as IQuestionVariation[])
					.map((q) => q.questionVariationId)
					.concat(questionVariationId);

				// remove siblings and add new selection
				const selectedQuestionVariationIds = state.selectedQuestionVariationIds
					.filter((id) => siblingVariationIds.indexOf(id) === -1)
					.concat(action.payload.questionVariation.questionVariationId);

				// Adding question after setting the variation text and id.
				const addedQuestions = state.addedQuestions
					.filter((q) => q.questionId !== action.payload.question.questionId)
					.concat({
						...action.payload.question,
						text: action.payload.questionVariation.text,
						questionVariationId: action.payload.questionVariation.questionVariationId,
						sourceType: SourceType.Library,
					});
				return { ...state, selectedQuestionVariationIds, addedQuestions };
			} else {
				const selectedQuestionVariationIds = state.selectedQuestionVariationIds.filter(
					(id) => id !== action.payload.questionVariation.questionVariationId,
				);
				const addedQuestions = state.addedQuestions.filter(
					(q) => q.questionVariationId !== action.payload.questionVariation.questionVariationId,
				);
				return { ...state, selectedQuestionVariationIds, addedQuestions };
			}
		}
		case ActionTypes.CUSTOM_QUESTION_SELECTED: {
			if (action.payload.isSelected) {
				const selectedCustomQuestionIds = [...state.selectedCustomQuestionIds].concat([action.payload.question.questionId]);
				const addedQuestionsWithCustomQuestion: IAddedQuestion[] = [...state.addedQuestions];
				const addedCustomQuestion: IAddedQuestion = {
					...action.payload.question,
					parentQuestionId: action.payload.parentQuestionId,
					sourceType: SourceType.Custom,
				};
				addedQuestionsWithCustomQuestion.push(addedCustomQuestion);
				return { ...state, selectedCustomQuestionIds, addedQuestions: addedQuestionsWithCustomQuestion };
			} else {
				const selectedCustomQuestionIds = state.selectedCustomQuestionIds.filter((id: number) => id !== action.payload.question.questionId);
				const addedQuestionsWithoutCustomQuestion: IAddedQuestion[] = state.addedQuestions.filter(
					(q: IAddedQuestion) => q.questionId !== action.payload.question.questionId,
				);
				return { ...state, selectedCustomQuestionIds, addedQuestions: addedQuestionsWithoutCustomQuestion };
			}
		}
		case ActionTypes.CUSTOM_QUESTION_SAVE_SUCCEEDED: {
			const customQuestion = action.payload.question;
			const addedQuestions = [...state.addedQuestions];

			// NOTE: there are some problems here:
			// 1. customQuestion.defaultCultureCode is never populated
			// 2. this should not run after a custom question has been updated directly from the survey
			//    list (by clicking the pencil icon, not opening the modal with the Click to add... button)

			// if the question is not active, there is no additional work needed to manage the custom question
			if (customQuestion.surveyQuestionId !== undefined && customQuestion.isActive) {
				const { defaultCultureCode, translations } = customQuestion;
				const englishTranslation = customQuestion.translations.find((i: ITranslationEditItem) => i.cultureCode === defaultCultureCode);
				const languagesWithTranslations = translations
					.filter((i: ITranslationEditItem) => i.textValue !== '' && i.cultureCode !== defaultCultureCode)
					.map((i: ITranslationEditItem) => i.cultureCode);

				const question: IAddedQuestion = {
					questionId: customQuestion.surveyQuestionId,
					isDemographic: customQuestion.isDemographic,
					questionType: customQuestion.questionType,
					text: englishTranslation ? englishTranslation.textValue : '',
					code: '',
					languages: languagesWithTranslations,
					sourceType: SourceType.Custom,
					attributeName: customQuestion.attributeName,
					externalAttributeColumnName: customQuestion.externalAttributeColumnName,
					parentQuestionId: action.payload.parentQuestionId,
				};

				// - always add/update custom question record on addedQuestions, there may be data changes
				// - the consumer is required to understand that a custom question is never added in
				// 		the same way a library question is, whether or not the question was just created or being edited
				if (!addedQuestions.find((iaq: IAddedQuestion) => iaq.questionId === customQuestion.surveyQuestionId)) {
					return {
						...state,
						addedQuestions: addedQuestions.concat([question]),
						selectedCustomQuestionIds: state.selectedCustomQuestionIds.concat([customQuestion.surveyQuestionId]),
						disableClose: true,
					};
				} else {
					const updatedMappedQuestions = addedQuestions.map((q: IAddedQuestion) =>
						q.questionId === question.questionId ? question : { ...q },
					);
					return { ...state, addedQuestions: updatedMappedQuestions, disableClose: true };
				}
			}
			return { ...state };
		}
		case ActionTypes.CUSTOM_QUESTIONS_GET_SUCCEEDED: {
			const checkOnCustomQuestionIds = state.selectedCustomQuestionIds;
			const customQuestions: ICustomQuestion[] = action.payload;
			const updatedAddedQuestions = state.addedQuestions.map((q: IAddedQuestion) => {
				if (checkOnCustomQuestionIds.indexOf(q.questionId) > -1) {
					const addCustomQuestion = customQuestions.find((c: ICustomQuestion) => c.questionId === q.questionId);
					if (!addCustomQuestion) {
						return q;
					}
					q.responseOptionGroup = addCustomQuestion.responseOptionGroup;
					q.responseOptions = addCustomQuestion.responseOptions;
					q.text = addCustomQuestion.text;
					q.languages = addCustomQuestion.languages;
					q.externalAttributeColumnName = addCustomQuestion.externalAttributeColumnName;
				}
				return q;
			});
			return { ...state, addedQuestions: updatedAddedQuestions, disableClose: false };
		}
		case CommonQuestionActionTypes.QUESTION_LANGUAGES_BG_GET_SUCCEEDED: {
			const checkOnCustomQuestionIds = state.selectedCustomQuestionIds;
			const customQuestions = action.payload.filter((q) => q.sourceType === CommonSourceType.Custom);
			const updatedAddedQuestions = state.addedQuestions.map((q: IAddedQuestion) => {
				if (checkOnCustomQuestionIds.indexOf(q.questionId) > -1) {
					const addCustomQuestion = customQuestions.find((c: any) => c.questionId === q.questionId);
					if (!addCustomQuestion) {
						return q;
					}
					q.responseOptionGroup = addCustomQuestion.responseOptionGroup;
					q.responseOptions = addCustomQuestion.responseOptions;
					q.text = addCustomQuestion.text;
					q.languages = addCustomQuestion.languages;
				}
				return q;
			});
			return { ...state, addedQuestions: updatedAddedQuestions, disableClose: false };
		}
	}

	return state;
};
